/**
 * shoppingListUtils.js — Agrégation "intelligente" d'ingrédients en texte libre
 * (recettes personnelles, repas du calendrier) en liste de courses catégorisée avec
 * sommation des quantités identiques. Utilisé par SmartShoppingListModal.jsx (16/09/2026).
 */
const CATEGORY_KEYWORDS = {
  'Protéines': ['poulet', 'boeuf', 'bœuf', 'dinde', 'porc', 'veau', 'poisson', 'saumon', 'thon', 'crevette', 'oeuf', 'œuf', 'tofu', 'seitan', 'jambon', 'viande', 'steak', 'filet', 'escalope', 'proteine', 'whey', 'cabillaud', 'merlu', 'sardine', 'blanc de poulet'],
  'Produits laitiers': ['fromage', 'yaourt', 'yogourt', 'lait', 'skyr', 'cottage', 'mozzarella', 'parmesan', 'creme', 'beurre', 'faisselle', 'sere', 'mascarpone'],
  'Glucides': ['riz', 'pates', 'pâtes', 'pain', 'avoine', 'flocons', 'quinoa', 'patate', 'pomme de terre', 'semoule', 'boulgour', 'tortilla', 'cereales', 'biscotte', 'farine'],
  'Légumes & fruits': ['tomate', 'carotte', 'brocoli', 'courgette', 'poivron', 'epinard', 'salade', 'oignon', 'ail', 'citron', 'pomme', 'banane', 'fruit', 'legume', 'champignon', 'concombre', 'avocat', 'myrtille', 'fraise', 'framboise', 'mangue', 'poire', 'haricot', 'petit pois', 'courge'],
  'Lipides & oléagineux': ['huile', 'amande', 'noix', 'beurre de cacahuete', 'olive', 'graine', 'noisette', 'pistache', 'cacahuete'],
  'Épices & divers': ['sel', 'poivre', 'epice', 'herbe', 'cumin', 'paprika', 'curry', 'vanille', 'miel', 'sirop', 'cannelle', 'moutarde', 'vinaigre', 'sauce', 'bouillon', 'levure'],
};

const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const categorize = (name) => {
  const n = normalize(name);
  for (const [cat, words] of Object.entries(CATEGORY_KEYWORDS)) {
    if (words.some((w) => n.includes(normalize(w)))) return cat;
  }
  return 'Autres';
};

/* ---------- Fractions & unités multi-mots (ajouté session 27) ----------
 * "1/4 c. à café", "1/2 c. à soupe", "¾ tasse" : la quantité peut être une fraction (chiffre
 * ou caractère unicode ¼ ½ ¾ etc.), et l'unité peut contenir des espaces (cuillère à café/soupe)
 * — non gérés par le parseur d'origine (qui ne captait qu'un seul "mot" d'unité, sans fraction). */
const UNICODE_FRACTIONS = { '¼': '1/4', '½': '1/2', '¾': '3/4', '⅓': '1/3', '⅔': '2/3', '⅛': '1/8', '⅜': '3/8', '⅝': '5/8', '⅞': '7/8' };

const parseQtyToken = (token) => {
  const frac = token.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
  const dec = token.match(/^\d+(?:[.,]\d+)?$/);
  if (dec) return parseFloat(token.replace(',', '.'));
  return null;
};

const SINGLE_TOKEN_UNITS = {
  kg: 'kg', g: 'g', ml: 'ml', cl: 'cl', dl: 'dl', l: 'l',
  cac: 'c. à café', cas: 'c. à soupe', cc: 'c. à café', cs: 'c. à soupe',
  pincee: 'pincée', pincees: 'pincée', gousse: 'gousse', gousses: 'gousse',
  tranche: 'tranche', tranches: 'tranche', unite: 'unité', unites: 'unité',
  sachet: 'sachet', sachets: 'sachet', portion: 'portion', portions: 'portion',
};

/** Parse "1/4 c. à café de sel" -> { qty: 0.25, unit: 'c. à café', name: 'sel' }. Gère aussi
 * les fractions unicode (¼ ½ ¾...) et les quantités décimales classiques ("120 g de riz").
 * L'unité est détectée mot par mot (normalisé, sans ponctuation) pour gérer proprement les
 * formes multi-mots ("cuillère à café/soupe", "c. à café/soupe") sans piéger le début d'un
 * mot comme "café" avec une frontière de regex mal placée. */
export const parseIngredientString = (raw) => {
  let s = (raw || '').trim();
  Object.entries(UNICODE_FRACTIONS).forEach(([u, f]) => { s = s.split(u).join(`${f} `); });
  const m = s.match(/^(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s+(.+)$/);
  if (!m) return { qty: null, unit: '', name: s };
  const qty = parseQtyToken(m[1].replace(/\s+/g, ''));
  const rest = m[2].trim();
  const restWords = rest.split(/\s+/);
  const w = restWords.map((tok) => normalize(tok).replace(/\./g, ''));
  const stripLeadDe = (name) => name.replace(/^(?:de\s+|d['’])/i, '').trim();

  if (w.length >= 3 && (w[0] === 'cuillere' || w[0] === 'cuilleres') && w[1] === 'a' && w[2].startsWith('cafe')) {
    return { qty, unit: 'c. à café', name: stripLeadDe(restWords.slice(3).join(' ')) || rest };
  }
  if (w.length >= 3 && (w[0] === 'cuillere' || w[0] === 'cuilleres') && w[1] === 'a' && w[2].startsWith('soupe')) {
    return { qty, unit: 'c. à soupe', name: stripLeadDe(restWords.slice(3).join(' ')) || rest };
  }
  if (w.length >= 3 && w[0] === 'c' && w[1] === 'a' && w[2].startsWith('c')) {
    return { qty, unit: 'c. à café', name: stripLeadDe(restWords.slice(3).join(' ')) || rest };
  }
  if (w.length >= 3 && w[0] === 'c' && w[1] === 'a' && w[2].startsWith('s')) {
    return { qty, unit: 'c. à soupe', name: stripLeadDe(restWords.slice(3).join(' ')) || rest };
  }
  if (SINGLE_TOKEN_UNITS[w[0]]) {
    return { qty, unit: SINGLE_TOKEN_UNITS[w[0]], name: stripLeadDe(restWords.slice(1).join(' ')) || rest };
  }
  return { qty, unit: '', name: stripLeadDe(rest) };
};

/** Reconvertit une quantité décimale en fraction lisible si proche d'une fraction usuelle
 * (ex: 0.5 -> "1/2", 1.25 -> "1 1/4"), sinon arrondit à 1 décimale. */
const NICE_FRACTIONS = [[1 / 3, '1/3'], [1 / 4, '1/4'], [1 / 2, '1/2'], [2 / 3, '2/3'], [3 / 4, '3/4']];
const formatQty = (q) => {
  const whole = Math.floor(q);
  const frac = q - whole;
  const nice = NICE_FRACTIONS.find(([v]) => Math.abs(frac - v) < 0.02);
  if (nice && frac > 0.02) return whole > 0 ? `${whole} ${nice[1]}` : nice[1];
  const rounded = Math.round(q * 10) / 10;
  return rounded % 1 === 0 ? String(Math.round(rounded)) : String(rounded);
};

/** Fusionne des ingrédients bruts en liste groupée par catégorie, quantités sommées. */
export const buildSmartShoppingList = (rawIngredients) => {
  const map = new Map();
  (rawIngredients || []).filter(Boolean).forEach((raw) => {
    const { qty, unit, name } = parseIngredientString(raw);
    if (!name) return;
    const key = normalize(name);
    if (!map.has(key)) map.set(key, { name, category: categorize(name), entries: [] });
    map.get(key).entries.push({ qty, unit });
  });

  const items = Array.from(map.values()).map((item) => {
    const byUnit = {};
    let freeformCount = 0;
    item.entries.forEach(({ qty, unit }) => {
      if (qty != null && unit) byUnit[unit] = (byUnit[unit] || 0) + qty;
      else if (qty != null) byUnit['×'] = (byUnit['×'] || 0) + qty;
      else freeformCount += 1;
    });
    const parts = Object.entries(byUnit).map(([u, q]) => (u === '×' ? `${formatQty(q)}x` : `${formatQty(q)} ${u}`));
    if (freeformCount) parts.push(`${freeformCount}x`);
    return { name: item.name, category: item.category, qtyLabel: parts.join(' + ') };
  });

  const grouped = {};
  items.forEach((it) => {
    grouped[it.category] = grouped[it.category] || [];
    grouped[it.category].push(it);
  });
  Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
  return grouped;
};
